import Bugsnag from '@bugsnag/js';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import colors from 'styles/colors';
import { jobStatusIcons, jobStatusLabels } from 'constants/jobs';
import { isJobInProgress, splitName, toNumberOrDefault } from 'utils/helpers';
import truncateText from 'utils/truncateText';
import getColorForName from 'utils/nameColorPicker';
import withPopover from 'components/hoc/withPopoverMenu';
import Paper from 'components/system/Paper';
import NoSearchResults from 'components/pages/Jobs/NoJobsFound';
import useNavigateWithTabSupport from 'hooks/useNavigateWithTabSupport';
import JobsRowActionMenu from './JobsRowActionMenu';
import JobsCommonFilters from './JobsCommonFilters';

const JobsList = ({
  jobs, page, total, rowsPerPage, columns, handleChangePage,
  handleChangeRowsPerPage, handleSearch, searchQuery: externalSearchQuery,
  onFilterChange, filters = {},
}) => {
  const { navigateWithTabSupport } = useNavigateWithTabSupport();

  const TableRowWithPopover = withPopover((props) => (
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        props.onClick && props.onClick(e);
      }}
      style={{ color: colors.text.primary }}
    >
      <MoreVertIcon />
    </IconButton>
  ));

  return (
    <Paper sx={{ overflowX: 'auto' }}>
      <JobsCommonFilters
        filters={filters}
        onFilterChange={onFilterChange}
        handleSearch={handleSearch}
        searchQuery={externalSearchQuery}
      />
      <Table>
        <TableHead>
          <TableRow>
            {
              columns.map(({ label }) => (
                <TableCell key={label} align="left">{label}</TableCell>
              ))
            }
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs?.length === 0 && externalSearchQuery ? (
            <TableRow>
              <TableCell colSpan={columns.length + 1}>
                <NoSearchResults />
              </TableCell>
            </TableRow>
          ) : (
            jobs?.map((job) => (
              <TableRow
                key={job.jobId}
                sx={{
                  '&:last-child th': { border: 0 },
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: `${colors.background.secondary}`,
                  },
                  '&:active': {
                    backgroundColor: `${colors.background.secondary}`,
                  },
                  '&:focused': {
                    backgroundColor: `${colors.background.secondary}`,
                  },
                }}
                onMouseDown={(event) => navigateWithTabSupport(event, '/jobs', job.jobId, {
                  jobId: job.jobId,
                  isNotebook: job.isNotebook,
                }, job.isNotebook ? { isNotebook: true } : {}, {
                  handleMiddleClickOnMouseDown: true,
                })}
                onMouseUp={(event) => navigateWithTabSupport(event, '/jobs', job.jobId, {
                  jobId: job.jobId,
                  isNotebook: job.isNotebook,
                }, job.isNotebook ? { isNotebook: true } : {}, {
                  handleMiddleClickOnMouseDown: false,
                })}
              >
                {
                  columns.map(({ key }) => {
                    if (key === 'runStatus') {
                      const Icon = jobStatusIcons[job[key]];
                      if (!Icon) {
                        Bugsnag.notify(new Error(`Unknown runStatus: ${job[key]}`));
                        return null;
                      }
                      return (
                        <TableCell key={key} align="left">
                          <Box sx={{ display: 'flex', alignItem: 'center' }}>
                            <Icon sx={{ marginRight: '10px' }} />
                            {jobStatusLabels[job[key]]}
                          </Box>
                        </TableCell>
                      );
                    }
                    if (key === 'jobName') {
                      const { text, tooltipText } = truncateText(job[key], 40);
                      return (
                        <TableCell
                          key={key}
                          align="left"
                          sx={{
                            maxWidth: '205px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {tooltipText ? (<Tooltip title={tooltipText} arrow><span>{text}</span></Tooltip>) : text}
                        </TableCell>
                      );
                    }

                    if (key === 'owner') {
                      const { firstName, lastName } = splitName(job[key]);
                      return (
                        <TableCell
                          key={key}
                          align="left"
                          sx={{
                            maxWidth: '180px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <div
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
                          >
                            <Avatar
                              alt={job.owner || 'owner'}
                              src={job.avatar}
                              style={{
                                marginRight: '10px',
                                fontSize: '14px',
                                backgroundColor: getColorForName(firstName, lastName),
                                width: '32px',
                                height: '32px',
                                textTransform: 'capitalize',
                              }}
                            >
                              {job.owner ? job.owner[0] : '-'}
                            </Avatar>
                            {job[key]}
                          </div>
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell key={key} align="left">
                        {job[key]}
                      </TableCell>
                    );
                  })
                }
                <TableCell
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  onMouseUp={(e) => { e.stopPropagation(); }}
                >
                  {/* <TableRowWithPopover> */}
                  {/*   <div style={{ width: '150px' }}> */}
                  {/*     <JobCancelButton */}
                  {/*        */}
                  {/*     /> */}
                  {/*   </div> */}
                  {/* </TableRowWithPopover> */}

                  <JobsRowActionMenu
                    jobId={job.jobId}
                    jobStatus={job.runStatus}
                    jobOwnerId={job.userId}
                    disabled={!isJobInProgress(job.runStatus)}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        sx={{ overflow: 'visible' }}
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={toNumberOrDefault(rowsPerPage, 10)}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default JobsList;
